import { FC, useCallback, useEffect, useState } from "react";
import { NetworkQualityLevel, VideoTrack } from "twilio-video";
import { createContext } from "use-context-selector";
import { getExistingVideoTrack } from "../components/helpers/getExistingTrack";
import { useRemoteParticipant } from "../hooks/useRemoteParticipant";
import { useRoom } from "../hooks/useRoom";
import { getTrack } from "../utils/getTrack";

export type RemoteParticipantContextType = {
  localRemoteVideoOff: boolean;
  localRemoteAudioOff: boolean;
  handleLocalRemoteVideo: (value: boolean) => void;
  handleLocalRemoteAudio: (value: boolean) => void;
  networkQualityLevelRemote: number;
  setNetworkQualityLevelRemote: (value: number) => void;
  isOnBackground: boolean;
  handleOnBackground: (value: boolean) => void;
};

export const RemoteParticipantContext = createContext(
  {} as RemoteParticipantContextType
);

export const RemoteParticipantContextProvider = ({ children }: { children: any }) => {
  const { remoteParticipant } = useRemoteParticipant();
  const { currentFocused, handleFocusedVideoOff } = useRoom();

  const [localRemoteVideoOff, setLocalRemoteVideoOff] =
    useState<boolean>(false);
  const [localRemoteAudioOff, setLocalRemoteAudioOff] =
    useState<boolean>(false);

  const [isOnBackground, setIsOnBackground] = useState<boolean>(false);

  useEffect(() => {
    if (currentFocused === "remote") handleFocusedVideoOff(localRemoteVideoOff);
  }, [currentFocused]);

  const handleLocalRemoteVideo = useCallback(
    (value: boolean) => {
      setLocalRemoteVideoOff(value);
      if (remoteParticipant && currentFocused === "remote") {
        if (value) {
          handleFocusedVideoOff(true);
        } else {
          const cameraTrack = getTrack(
            "camera",
            getExistingVideoTrack(remoteParticipant)
          ) as VideoTrack;
          if (cameraTrack.isEnabled) {
            handleFocusedVideoOff(false);
          }
        }
      }
    },
    [currentFocused, remoteParticipant]
  );

  const handleLocalRemoteAudio = useCallback((value: boolean) => {
    setLocalRemoteAudioOff(value);
  }, []);

  const [networkQualityLevelRemote, setNetworkQualityLevelRemote] =
    useState<number>(0);

  const handleNetworkQualityLevelChanged = useCallback(
    (qualityLevel: NetworkQualityLevel) => {
      setNetworkQualityLevelRemote(qualityLevel);
    },
    []
  );

  const handleOnBackground = useCallback((value: boolean) => {
    setIsOnBackground(value);
  }, []);

  useEffect(() => {
    if (remoteParticipant) {
      remoteParticipant.on(
        "networkQualityLevelChanged",
        handleNetworkQualityLevelChanged
      );
    }

    return () => {
      if (remoteParticipant) {
        remoteParticipant.off(
          "networkQualityLevelChanged",
          handleNetworkQualityLevelChanged
        );
      }
    };
  }, [remoteParticipant]);

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <RemoteParticipantContext.Provider
      value={{
        localRemoteVideoOff,
        localRemoteAudioOff,
        handleLocalRemoteVideo,
        handleLocalRemoteAudio,
        networkQualityLevelRemote,
        setNetworkQualityLevelRemote,
        isOnBackground,
        handleOnBackground,
      }}
    >
      {children}
    </RemoteParticipantContext.Provider>
  );
};
